/**
 * Wave 3.0a — Single source of truth for ALL currency conversion.
 * Reads dynamic rates from appConfig.exchange_rates_config.
 * JPY-pivoted: source → JPY → target.
 *
 * Supported currencies: JPY, USD, AED, USDC, EUR, GBP, GEL, RUB
 * New currencies can be added via appConfig (key: jpy_to_{lowercase})
 */

import { DB } from '../data/database';

// Safety net fallbacks — ONLY used if appConfig has no rates at all
const FALLBACK_RATES = {
  jpy_to_usd: 0.0067,
  jpy_to_aed: 0.0245,
  jpy_to_eur: 0.0062,
  jpy_to_gbp: 0.0053,
  jpy_to_gel: 0.018,
  jpy_to_rub: 0.61,
};

/**
 * Get the JPY → target currency rate.
 * Reads from appConfig.exchange_rates_config first, falls back to hardcoded.
 */
export function getJpyRate(currency) {
  const cur = String(currency || '').toUpperCase();
  if (cur === 'JPY') return 1;
  if (cur === 'USDC') return getJpyRate('USD');

  const key = `jpy_to_${cur.toLowerCase()}`;

  try {
    const config = DB.get('appConfig');
    const rates = config?.exchange_rates_config;
    if (rates && rates[key]) return Number(rates[key]);
  } catch (e) {
    console.warn('[currency] Failed to read appConfig rates:', e.message);
  }

  // Fallback
  if (FALLBACK_RATES[key]) return FALLBACK_RATES[key];
  console.error(`[currency] No rate found for ${cur} (key: ${key}). Check appConfig.exchange_rates_config.`);
  return 0;
}

/**
 * Convert amount from one currency to another (JPY-pivoted).
 * convertCurrency(1000000, 'JPY', 'USD') → ~6700
 * convertCurrency(100, 'USD', 'AED') → ~365
 */
export function convertCurrency(amount, fromCurrency, toCurrency) {
  if (amount == null || isNaN(Number(amount))) return 0;
  const num = Number(amount);
  const from = String(fromCurrency || 'JPY').toUpperCase();
  const to = String(toCurrency || 'JPY').toUpperCase();

  if (from === to) return num;
  if (from === 'USDC') return convertCurrency(num, 'USD', to);
  if (to === 'USDC') return convertCurrency(num, from, 'USD');

  // Convert source → JPY
  let jpyAmount;
  if (from === 'JPY') {
    jpyAmount = num;
  } else {
    const fromRate = getJpyRate(from);
    if (!fromRate) return 0;
    jpyAmount = num / fromRate;
  }

  // Convert JPY → target
  if (to === 'JPY') return jpyAmount;
  const toRate = getJpyRate(to);
  if (!toRate) return 0;
  return jpyAmount * toRate;
}

/**
 * Get the conversion rate from one currency to another.
 * getRate('JPY', 'USD') → 0.0067
 * getRate('USD', 'AED') → ~3.66
 */
export function getRate(fromCurrency, toCurrency) {
  return convertCurrency(1, fromCurrency, toCurrency);
}

/**
 * Format a JPY amount in the specified display currency.
 * formatPrice(1000000, 'USD') → "$6,700"
 * Moved from constants.js — now uses dynamic rates.
 */

// Currency display metadata (NO rates — use convertCurrency for conversion)
const CURRENCY_META = {
  JPY:  { symbol: '¥',    decimals: 0 },
  USD:  { symbol: '$',    decimals: 0 },
  AED:  { symbol: 'د.إ',  decimals: 0 },
  USDC: { symbol: 'USDC', decimals: 2 },
  EUR:  { symbol: '€',    decimals: 0 },
  GBP:  { symbol: '£',    decimals: 0 },
  GEL:  { symbol: '₾',    decimals: 0 },
  RUB:  { symbol: '₽',    decimals: 0 },
};

export function formatPrice(jpy, currency = 'JPY') {
  const cur = String(currency || 'JPY').toUpperCase();
  const meta = CURRENCY_META[cur] || { symbol: cur, decimals: 0 };
  const converted = convertCurrency(jpy, 'JPY', cur);
  return `${meta.symbol}${Math.round(converted).toLocaleString()}`;
}

/**
 * Default empty container costs structure.
 * All 5 fixed cost keys present, defaulting to JPY with 0 amount.
 */
export function defaultFixedCosts() {
  const now = new Date().toISOString();
  return {
    fixed: {
      freight:      { amount: 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: now },
      drayage:      { amount: 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: now },
      port_charges: { amount: 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: now },
      bl_fee:       { amount: 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: now },
      loading_fee:  { amount: 0, currency: 'JPY', notes: '', status: 'provisional', updated_at: now },
    },
    custom: [],
  };
}

/**
 * Calculate total container costs in JPY.
 * Sums all fixed costs + custom items, converting each to JPY.
 */
export function calculateContainerTotalJPY(costs) {
  if (!costs) return 0;
  let total = 0;
  if (costs.fixed) {
    Object.values(costs.fixed).forEach(c => {
      if (c && c.amount) {
        total += convertCurrency(Number(c.amount), c.currency || 'JPY', 'JPY');
      }
    });
  }
  if (costs.custom) {
    costs.custom.forEach(item => {
      const lineTotal = (Number(item.quantity) || 1) * (Number(item.unit_cost) || 0);
      total += convertCurrency(lineTotal, item.currency || 'JPY', 'JPY');
    });
  }
  return Math.round(total);
}
